import React from "react"
import { Button, Space, Popconfirm } from "antd"
import { EditOutlined, DeleteOutlined } from "@ant-design/icons"
import { ID } from "@/types/common"

export interface TableActionColumnProps<T = any> {
  /**
   * 编辑回调函数，接收当前行数据
   */
  onEdit?: (record: T) => void
  /**
   * 删除回调函数，接收当前行数据或ID
   */
  onDelete?: (id: ID) => void
  /**
   * 删除确认提示文本，支持使用 {name} 占位符
   */
  deleteConfirmText?: string | ((record: T) => string)
  /**
   * 删除确认描述文本
   */
  deleteConfirmDescription?: string | ((record: T) => string)
  /**
   * 是否显示编辑按钮
   */
  showEdit?: boolean
  /**
   * 是否显示删除按钮
   */
  showDelete?: boolean
  /**
   * 获取记录名称的函数，用于删除确认提示
   */
  getName?: (record: T) => string
  /**
   * 获取记录ID的函数
   */
  getId?: (record: T) => ID
  /**
   * 操作列宽度
   */
  width?: number
  /**
   * 是否固定在右侧
   */
  fixed?: "right" | "left"
}

/**
 * 通用的表格操作列组件
 * 提供编辑和删除功能，减少重复代码
 */
export function TableActionColumn<T = any>({
  onEdit,
  onDelete,
  deleteConfirmText = "确认删除",
  deleteConfirmDescription,
  showEdit = true,
  showDelete = true,
  getName,
  getId,
  width = 150,
  fixed = "right",
}: TableActionColumnProps<T>) {
  return {
    title: "操作",
    key: "action",
    width,
    fixed,
    render: (_: any, record: T) => {
      const recordName = getName
        ? getName(record)
        : (record as any)?.name || "该项"
      const recordId = getId ? getId(record) : (record as any)?.id

      const getConfirmText = () => {
        if (typeof deleteConfirmText === "function") {
          return deleteConfirmText(record)
        }
        return deleteConfirmText.replace("{name}", recordName)
      }

      const getConfirmDescription = () => {
        if (deleteConfirmDescription) {
          if (typeof deleteConfirmDescription === "function") {
            return deleteConfirmDescription(record)
          }
          return deleteConfirmDescription.replace("{name}", recordName)
        }
        return `确定要删除"${recordName}"吗？`
      }

      return (
        <Space size="small">
          {showEdit && onEdit && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            >
              编辑
            </Button>
          )}
          {showDelete && onDelete && (
            <Popconfirm
              title={getConfirmText()}
              description={getConfirmDescription()}
              onConfirm={() => onDelete(recordId)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    },
  }
}

export default TableActionColumn
